/**
 * 通用无限滚动列表组件（基础版）
 * 通过 emptyImgSize / textSize 定制空状态与提示文案样式。
 */
import { BASE_IMG_URL } from '@images/imageUrls';
import React, { useEffect, useRef, useCallback, useState } from 'react';
import styles from './index.module.scss';

export interface InfiniteScrollListProps {
    data: any[];
    loading: boolean;
    hasMore: boolean;
    onLoadMore: () => void;
    renderItem: (item: any, index: number) => React.ReactNode;
    threshold?: number;
    scrollableTarget?: React.RefObject<HTMLDivElement>;
    className?: string;
    listClassName?: string; // 列表自定义classname
    style?: React.CSSProperties;
    emptyText?: string;
    emptyIcon?: string;
    emptyBoxStyle?: any; // 为空状态时的自定义样式
    emptyActionText?: string; // 空态时的蓝色行动文案
    onEmptyAction?: () => void; // 空态时的蓝色文字点击回调
    emptyExtra?: React.ReactNode; // 空态文案下方的自定义插槽
    loadingText?: string;
    noMoreText?: string;
    errorText?: string;
    emptyImgSize?: number;
    textSize?: number;
    /** 是否启用虚拟列表（窗口渲染） */
    enableVirtual?: boolean;
    /** 预估的单条记录高度（像素），用于虚拟列表计算 */
    estimatedItemHeight?: number;
    /** 视口上下额外渲染的条数 */
    overscan?: number;
}

const InfiniteScrollListBase: React.FC<InfiniteScrollListProps> = ({
    data = [],
    loading = false,
    hasMore = true,
    onLoadMore,
    renderItem,
    threshold = 100,
    scrollableTarget,
    className,
    listClassName,
    style,
    emptyText = '暂无数据',
    emptyIcon = '',
    emptyBoxStyle = '',
    emptyActionText = '',
    onEmptyAction,
    emptyExtra,
    loadingText = '加载中...',
    noMoreText = '没有更多了',
    errorText,
    emptyImgSize = 100, // 仅保留兼容性，实际样式交给 className
    textSize = 14,
    enableVirtual = false,
    estimatedItemHeight,
    overscan = 3,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const lastRequestTimeRef = useRef(0);
    const lastScrollTopRef = useRef(0);
    const containerOffsetTopRef = useRef(0);
    const THROTTLE_GAP = 500;

    const [visibleRange, setVisibleRange] = useState<{ start: number; end: number }>(() => {
        const length = Array.isArray(data) ? data.length : 0;
        return { start: 0, end: length > 0 ? length - 1 : -1 };
    });

    const useVirtual = enableVirtual && !!estimatedItemHeight && Array.isArray(data) && data.length > 0;

    const updateVisibleRange = useCallback(
        (containerScrollTop: number, viewportHeight: number, dataLength: number) => {
            if (!useVirtual || !estimatedItemHeight || dataLength === 0) {
                setVisibleRange({ start: 0, end: dataLength > 0 ? dataLength - 1 : -1 });
                return;
            }

            const safeScrollTop = Math.max(containerScrollTop, 0);
            const total = dataLength;
            const startIndex = Math.max(Math.floor(safeScrollTop / estimatedItemHeight) - overscan, 0);
            const endIndex = Math.min(
                Math.ceil((safeScrollTop + viewportHeight) / estimatedItemHeight) + overscan,
                total - 1
            );

            setVisibleRange({ start: startIndex, end: endIndex });
        },
        [useVirtual, estimatedItemHeight, overscan]
    );

    const handleScroll = useCallback(() => {
        const scrollElement = scrollableTarget?.current || window;
        let scrollTop = 0;
        let scrollHeight = 0;
        let clientHeight = 0;
        let containerScrollTop = 0;

        if (scrollElement === window) {
            scrollTop = window.scrollY || document.documentElement.scrollTop;
            scrollHeight = document.documentElement.scrollHeight;
            clientHeight = window.innerHeight;
            containerScrollTop = scrollTop - (containerOffsetTopRef.current || 0);
        } else {
            const element = scrollElement as HTMLDivElement;
            scrollTop = element.scrollTop;
            scrollHeight = element.scrollHeight;
            clientHeight = element.clientHeight;
            containerScrollTop = scrollTop;
        }

        // 无论向上还是向下滚动，都需要及时更新虚拟列表的可见区域，避免出现空白
        updateVisibleRange(
            containerScrollTop,
            clientHeight,
            Array.isArray(data) ? data.length : 0,
        );

        // 以下逻辑仅用于触发“加载更多”，仍然只在向下滚动时生效
        const isScrollDown = scrollTop >= lastScrollTopRef.current;
        lastScrollTopRef.current = scrollTop;

        if (!isScrollDown || loading || !hasMore) return;

        const now = Date.now();
        if (now - lastRequestTimeRef.current < THROTTLE_GAP) return;

        if (scrollHeight - scrollTop - clientHeight <= threshold) {
            lastRequestTimeRef.current = now;
            onLoadMore();
        }
    }, [loading, hasMore, threshold, onLoadMore, scrollableTarget, updateVisibleRange, data]);

    useEffect(() => {
        const scrollElement = scrollableTarget?.current || window;

        // 记录容器在页面中的初始偏移量（仅 window 滚动时需要）
        if (scrollElement === window && containerRef.current) {
            containerOffsetTopRef.current = containerRef.current.offsetTop || 0;
        }

        if (scrollElement === window) {
            window.addEventListener('scroll', handleScroll);
            return () => {
                window.removeEventListener('scroll', handleScroll);
            };
        } else {
            scrollElement.addEventListener('scroll', handleScroll);
            return () => {
                scrollElement.removeEventListener('scroll', handleScroll);
            };
        }
    }, [handleScroll, scrollableTarget]);

    // data 长度变动时，重新计算可见区域
    useEffect(() => {
        const scrollElement = scrollableTarget?.current || window;
        let clientHeight = 0;
        let containerScrollTop = 0;

        if (scrollElement === window) {
            clientHeight = window.innerHeight;
            const scrollTop = window.scrollY || document.documentElement.scrollTop;
            containerScrollTop = scrollTop - (containerOffsetTopRef.current || 0);
        } else {
            const element = scrollElement as HTMLDivElement;
            clientHeight = element.clientHeight;
            containerScrollTop = element.scrollTop;
        }

        updateVisibleRange(containerScrollTop, clientHeight, Array.isArray(data) ? data.length : 0);
    }, [data?.length, scrollableTarget, updateVisibleRange]);

    // 判断是否显示空态
    const showEmpty = !loading && data.length === 0 && (emptyText || emptyIcon);
    const showStatusBox = data.length > 0 && (errorText || loading || (!loading && !hasMore && !!noMoreText));

    return (
        <div ref={containerRef} className={className} style={style}>
            {/* 列表为空 */}
            {showEmpty && (
                <div className={styles.emptyBox} style={emptyBoxStyle ? emptyBoxStyle : {}}>
                    {emptyIcon && (
                        <img
                            src={emptyIcon}
                            alt=""
                            className={styles.emptyImg}
                        />
                    )}
                    <div className={styles.emptyContent}>
                        <span className={styles.emptyText}>{emptyText}</span>
                        {emptyActionText && onEmptyAction && (
                            <>
                                <span className={styles.emptyTextGray}>，</span>
                                <span className={styles.emptyAction} onClick={onEmptyAction}>{emptyActionText}</span>
                            </>
                        )}
                    </div>
                    {emptyExtra && (
                        <div className={styles.emptyExtra}>
                            {emptyExtra}
                        </div>
                    )}
                </div>
            )}

            {/* 列表内容 */}
            {data.length > 0 && (() => {
                if (!useVirtual || !estimatedItemHeight) {
                    return (
                        <div className={listClassName}>
                            {data.map((item, index) => (
                                <div key={item.id || index}>{renderItem(item, index)}</div>
                            ))}
                        </div>
                    );
                }

                const totalHeight = data.length * estimatedItemHeight;
                const start = Math.max(visibleRange.start, 0);
                const end = visibleRange.end >= start ? visibleRange.end : start;
                const itemsToRender = data.slice(start, end + 1);

                return (
                    <div style={{ position: 'relative', height: totalHeight }}>
                        {itemsToRender.map((item, idx) => {
                            const actualIndex = start + idx;
                            return (
                                <div
                                    key={item.id || actualIndex}
                                    style={{
                                        position: 'absolute',
                                        top: actualIndex * estimatedItemHeight,
                                        left: 0,
                                        right: 0,
                                    }}
                                >
                                    {renderItem(item, actualIndex)}
                                </div>
                            );
                        })}
                    </div>
                );
            })()}

            {/* 加载状态和提示 - 仅在有数据时显示 */}
            {showStatusBox && (
                <div className={styles.statusBox}>
                    {errorText && <div className={styles.statusError}>{errorText}</div>}
                    {!errorText && loading && <div>{loadingText}</div>}
                    {!errorText && !loading && !hasMore && !!noMoreText && <div>{noMoreText}</div>}
                </div>
            )}
        </div>
    );
};

export default InfiniteScrollListBase;
